import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Home, Mail, Phone, MapPin, DollarSign, ArrowRight, CheckCircle2, Sparkles } from 'lucide-react';
import { cn } from '../lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LEADS_API = `${BACKEND_URL}/api/leads`;

const LOAN_INTENTS = [
  { v: 'Purchase', label: 'Buy a home' },
  { v: 'Refi', label: 'Refinance' },
  { v: 'HELOC', label: 'Home equity (HELOC)' },
  { v: 'Investment', label: 'Investment property' },
];

export default function Apply() {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState({
    firstName: '', lastName: '', email: '', phone: '',
    city: '', loanIntent: 'Purchase', estAmount: '', notes: '',
  });

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  useEffect(() => {
    const prev = document.title;
    document.title = 'Apply — Kinfolk Home Loans';
    return () => { document.title = prev; };
  }, []);

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        name: `${data.firstName} ${data.lastName}`.trim(),
        email: data.email,
        phone: data.phone,
        city: data.city,
        loanIntent: data.loanIntent,
        source: 'Website Form',
        temp: 'hot',
        score: 80,
        nextAction: 'First call \u2014 ASAP',
        nextActionAt: 'Now',
        estAmount: data.estAmount ? Number(String(data.estAmount).replace(/[^\d]/g, '')) : null,
        notes: data.notes,
        tags: ['Web lead', 'Inbound'],
      };
      await axios.post(LEADS_API, payload);
      setSubmitted(true);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  const canStep1 = data.firstName.trim() && data.lastName.trim() && data.email.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email);
  const canStep2 = data.phone.trim() && data.city.trim();

  return (
    <div className="min-h-screen bg-[hsl(0_0%_98%)] flex flex-col">
      {/* Top bar */}
      <header className="px-6 lg:px-12 py-5 flex items-center justify-between border-b hairline bg-white">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-xl bg-neutral-900 text-[hsl(var(--kf-gold))] flex items-center justify-center">
            <Home className="h-4 w-4" strokeWidth={2.5} />
          </div>
          <div className="leading-tight">
            <div className="brand-word text-[12px] text-neutral-900">Kinfolk</div>
            <div className="text-[10.5px] text-neutral-500 -mt-0.5">Home Loans</div>
          </div>
        </div>
        <div className="hidden md:flex items-center gap-1.5 text-[11px] text-neutral-500">
          <Sparkles className="h-3 w-3 text-[hsl(var(--kf-gold))]" />
          NMLS #2336233 · Equal Housing Lender
        </div>
      </header>

      {/* Hero + Form */}
      <main className="flex-1 grid lg:grid-cols-12 gap-0">
        {/* Left: hero copy */}
        <aside className="lg:col-span-5 relative overflow-hidden bg-neutral-900 text-white px-8 lg:px-14 py-14 lg:py-20 noise">
          <div className="absolute inset-0 bg-grid-dark" />
          <div className="absolute -right-24 -bottom-24 h-80 w-80 rounded-full bg-[hsl(var(--kf-gold))]/30 blur-[110px]" />
          <div className="relative max-w-md">
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-[hsl(var(--kf-gold))] font-bold">Get pre-approved</div>
            <h1 className="mt-3 display-xl text-[40px] lg:text-[52px] font-bold leading-[1.02] tracking-tight">
              Your home, <span className="gold-text">done right</span>.
            </h1>
            <p className="mt-4 text-[15px] text-white/70 leading-relaxed">
              Two minutes. No credit pull. A real human gets back to you in under an hour.
            </p>
            <div className="mt-10 grid gap-4 max-w-sm">
              <Pillar n="01" t="Tell us about you" s="Name + how to reach you." active={step >= 1} />
              <Pillar n="02" t="Your numbers" s="City, intent, ballpark loan size." active={step >= 2} />
              <Pillar n="03" t="Done — we'll call" s="A loan officer reviews and reaches out." active={submitted} />
            </div>
          </div>
        </aside>

        {/* Right: form */}
        <section className="lg:col-span-7 px-6 lg:px-16 py-12 lg:py-20 flex items-start justify-center">
          <div className="w-full max-w-xl">
            {!submitted ? (
              <>
                {/* Step header */}
                <div className="flex items-center gap-2 text-[11px] text-neutral-500 mb-1">
                  <span className="mono font-bold text-neutral-900">{String(step).padStart(2, '0')}</span>
                  <span className="text-neutral-300">/</span>
                  <span>02</span>
                </div>
                <h2 className="display text-[28px] font-bold tracking-tight text-neutral-900">
                  {step === 1 ? 'Who are we talking to?' : 'A few quick numbers'}
                </h2>
                <p className="text-[13px] text-neutral-500 mt-1">
                  {step === 1 ? 'We\'ll never spam you. Promise.' : 'Just a ballpark \u2014 nothing is locked in.'}
                </p>

                {/* Step 1 */}
                {step === 1 && (
                  <div className="mt-8 space-y-4 rise-in">
                    <div className="grid grid-cols-2 gap-4">
                      <Input label="First name" value={data.firstName} onChange={(v) => set('firstName', v)} testid="apply-firstName" />
                      <Input label="Last name" value={data.lastName} onChange={(v) => set('lastName', v)} testid="apply-lastName" />
                    </div>
                    <Input label="Email" type="email" icon={Mail} value={data.email} onChange={(v) => set('email', v)} placeholder="you@email.com" testid="apply-email" />
                    <Input label="Phone (optional now)" icon={Phone} value={data.phone} onChange={(v) => set('phone', v)} placeholder="(555) 123-4567" testid="apply-phone" />

                    <div className="pt-4 flex items-center justify-end">
                      <button
                        data-testid="apply-next-1"
                        disabled={!canStep1}
                        onClick={() => setStep(2)}
                        className="h-11 px-5 rounded-md btn-ink text-[13px] font-bold flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        Next <ArrowRight className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}

                {/* Step 2 */}
                {step === 2 && (
                  <div className="mt-8 space-y-4 rise-in">
                    {!data.phone && (
                      <Input label="Phone" icon={Phone} value={data.phone} onChange={(v) => set('phone', v)} placeholder="(555) 123-4567" required testid="apply-phone-2" />
                    )}
                    <Input label="City, State" icon={MapPin} value={data.city} onChange={(v) => set('city', v)} placeholder="Austin, TX" testid="apply-city" required />

                    <div>
                      <label className="text-[10.5px] uppercase tracking-wider text-neutral-500 font-bold">What&apos;s your goal?</label>
                      <div className="mt-2 grid grid-cols-2 gap-2">
                        {LOAN_INTENTS.map((o) => (
                          <button
                            key={o.v}
                            data-testid={`apply-intent-${o.v.toLowerCase()}`}
                            onClick={() => set('loanIntent', o.v)}
                            className={cn(
                              'h-11 rounded-md border text-[12.5px] font-semibold transition',
                              data.loanIntent === o.v
                                ? 'bg-neutral-900 text-white border-neutral-900'
                                : 'bg-white hairline text-neutral-700 hover:border-neutral-400'
                            )}
                          >
                            {o.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <Input
                      label="Estimated loan amount (optional)"
                      icon={DollarSign}
                      value={data.estAmount}
                      onChange={(v) => set('estAmount', v)}
                      placeholder="450,000"
                      mono
                      testid="apply-estAmount"
                    />

                    <div>
                      <label className="text-[10.5px] uppercase tracking-wider text-neutral-500 font-bold">Anything we should know? (optional)</label>
                      <textarea
                        data-testid="apply-notes"
                        rows={3}
                        value={data.notes}
                        onChange={(e) => set('notes', e.target.value)}
                        placeholder="House hunting in zip 78703, offer deadline Friday..."
                        className="mt-1 w-full rounded-md bg-white border hairline text-[13px] px-3 py-2 placeholder:text-neutral-400 focus:outline-none focus:border-neutral-900"
                      />
                    </div>

                    {error && (
                      <div className="rounded-md border border-red-200 bg-red-50 text-red-700 text-[12px] px-3 py-2">
                        {error}
                      </div>
                    )}

                    <div className="pt-4 flex items-center justify-between">
                      <button
                        data-testid="apply-back"
                        onClick={() => setStep(1)}
                        className="h-11 px-4 rounded-md border hairline bg-white text-[12.5px] font-semibold text-neutral-700 hover:border-neutral-400"
                      >
                        Back
                      </button>
                      <button
                        data-testid="apply-submit"
                        disabled={!canStep2 || submitting}
                        onClick={submit}
                        className="h-11 px-5 rounded-md btn-gold text-[13px] font-bold flex items-center gap-2 disabled:opacity-50"
                      >
                        {submitting ? 'Sending\u2026' : (<>Get my pre-approval <ArrowRight className="h-4 w-4" /></>)}
                      </button>
                    </div>
                  </div>
                )}

                <p className="mt-10 text-[10.5px] text-neutral-400 leading-relaxed">
                  By submitting, you agree to be contacted by Kinfolk Home Loans. No credit will be pulled.
                  TCPA · Privacy Policy · NMLS #2336233.
                </p>
              </>
            ) : (
              <div className="rise-in">
                <div className="h-14 w-14 rounded-2xl bg-[hsl(var(--kf-gold))] text-neutral-900 flex items-center justify-center">
                  <CheckCircle2 className="h-7 w-7" strokeWidth={2.5} />
                </div>
                <h2 className="mt-6 display-xl text-[40px] font-bold tracking-tight text-neutral-900 leading-[1.05]">
                  We&apos;ve got you, {data.firstName}.
                </h2>
                <p className="mt-3 text-[14px] text-neutral-600 max-w-md leading-relaxed">
                  Your application is in. A senior loan officer will call you at <span className="mono font-semibold text-neutral-900">{data.phone}</span> within the hour.
                </p>
                <div className="mt-8 rounded-2xl border hairline bg-white p-5">
                  <div className="text-[10.5px] uppercase tracking-[0.22em] text-neutral-500 font-bold">What happens next</div>
                  <ul className="mt-3 space-y-2.5 text-[13px] text-neutral-700">
                    <Step n="1" t="A real human reviews your details — within minutes." />
                    <Step n="2" t="We call you to talk through programs, rates, and your goal." />
                    <Step n="3" t="You get a pre-approval letter, ready to make offers with." />
                  </ul>
                </div>
                <a href="/" className="mt-10 inline-flex items-center gap-1.5 text-[12.5px] font-bold text-neutral-900 gold-underline">
                  Back to kinfolkhomeloans.com
                </a>
              </div>
            )}
          </div>
        </section>
      </main>

      <footer className="px-6 lg:px-12 py-5 border-t hairline bg-white text-[11px] text-neutral-500 flex flex-wrap items-center justify-between gap-2">
        <span>© 2026 Kinfolk Home Loans LLC</span>
        <span className="mono">NMLS #2336233 · Equal Housing Lender</span>
      </footer>
    </div>
  );
}

function Pillar({ n, t, s, active }) {
  return (
    <div className={cn(
      'flex items-start gap-3 rounded-xl border px-3.5 py-3 transition',
      active ? 'border-[hsl(var(--kf-gold))]/60 bg-white/[0.04]' : 'border-white/10'
    )}>
      <div className={cn(
        'h-8 w-8 rounded-lg flex items-center justify-center mono text-[11px] font-bold shrink-0',
        active ? 'bg-[hsl(var(--kf-gold))] text-neutral-900' : 'bg-white/10 text-white/60'
      )}>
        {n}
      </div>
      <div>
        <div className="text-[13px] font-bold leading-tight">{t}</div>
        <div className="text-[11px] text-white/55 mt-0.5">{s}</div>
      </div>
    </div>
  );
}

function Step({ n, t }) {
  return (
    <li className="flex items-start gap-3">
      <span className="h-5 w-5 rounded-full bg-neutral-900 text-[hsl(var(--kf-gold))] mono text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">{n}</span>
      <span>{t}</span>
    </li>
  );
}

function Input({ label, value, onChange, placeholder, type = 'text', icon: Icon, mono, required, testid }) {
  return (
    <div>
      <label className="text-[10.5px] uppercase tracking-wider text-neutral-500 font-bold">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <div className="mt-1 relative">
        {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-neutral-400" />}
        <input
          data-testid={testid}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={cn(
            'w-full h-11 rounded-md bg-white border hairline text-[14px] text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:border-neutral-900 transition',
            Icon ? 'pl-9 pr-3' : 'px-3',
            mono && 'mono'
          )}
        />
      </div>
    </div>
  );
}
